import React, { useEffect, useMemo } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { ContentCard, Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import CircularProgress from '@mui/material/CircularProgress'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { StyledBiennale } from 'components'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { displayDuration, useCycleDurationTimer } from '../rewards.logic'
import { useLazyGetTotalRewardToClaimQuery } from 'state/services/indexNodeRewards/indexNodeRewards'
import { useLifetimeTokens } from 'state/global/hooks'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'

dayjs.extend(utc)

const addLeadingZeros = (timeString: string): string => {
  const parts = timeString.split(' ')

  let hours = 0
  let minutes = 0
  let seconds = 0

  // Extract values from timeString where units might be omitted if their value is 0
  parts.forEach((part) => {
    const value = parseInt(part, 10)
    // Check the unit type and update corresponding variable
    if (part.includes('h')) {
      hours = value
    } else if (part.includes('m')) {
      minutes = value
    } else if (part.includes('s')) {
      seconds = value
    }
  })

  // Ensure leading zeros are added to single-digit values for hours, minutes, and seconds
  const formattedHours = hours < 10 ? `0${hours}` : String(hours)
  const formattedMinutes = minutes < 10 ? `0${minutes}` : String(minutes)
  const formattedSeconds = seconds < 10 ? `0${seconds}` : String(seconds)

  return `${formattedHours} : ${formattedMinutes} : ${formattedSeconds}`
}

const ProgressContainer = styled(Flex)`
  position: relative;
  align-self: center;
  align-items: center;
  justify-content: center;
  width: 162px;
  height: 162px;

  ${({ theme }) => theme.mediaQueries.tablet} {
    width: 255px;
    height: 255px;
  }
`

const Circle = styled(Flex)`
  align-items: center;
  justify-content: center;
`

const ProgressWrapper = styled(Flex)<{ isValueNegative?: boolean }>`
  position: absolute;
  transform: scaleX(-1);
`

const Progress = styled(CircularProgress)`
  &.MuiCircularProgress-root {
    color: ${({ theme }) => theme.colors.purple};
    .MuiCircularProgress-svg {
      .MuiCircularProgress-circle {
        stroke-linecap: round;
      }
    }
  }
`

const ProgressShadow = styled(CircularProgress)`
  &.MuiCircularProgress-root {
    color: ${({ theme }) => theme.colors.gray10};
    .MuiCircularProgress-svg {
      .MuiCircularProgress-circle {
        stroke-linecap: round;
      }
    }
  }
`

const DotBig = styled(Flex)`
  width: 20px;
  height: 20px;
  background-color: ${({ theme }) => theme.colors.backgroundContrast};
  border-radius: 50%;
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;

  ${({ theme }) => theme.mediaQueries.tablet} {
    width: 34px;
    height: 34px;
    top: -9px;
  }
`

const DotSmall = styled(DotBig)`
  width: 12px;
  height: 12px;
  top: -2px;
  background-color: ${({ theme }) => theme.colors.purple};
  z-index: 3;

  ${({ theme }) => theme.mediaQueries.tablet} {
    width: 20px;
    height: 20px;
  }
`

const UntilNewCycleCounter: React.FC = () => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const isTablet = useThemeMediaQuery('tablet')
  const { timeUntilEnd, progress, endOfCycle } = useCycleDurationTimer()
  const endOfCycleDeps = JSON.stringify(dayjs(endOfCycle).format('DD/MM/YYYY HH:mm'))
  const [getRewardFromService] = useLazyGetTotalRewardToClaimQuery()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const progressSize = useMemo(() => {
    if (isTablet) return 255
    return 162
  }, [isTablet])

  const formattedEndOfCycleDateToDisplay = useMemo(() => {
    if (!endOfCycle) return ''
    return dayjs(endOfCycle).utc().format('h:mm A [UTC]')
  }, [endOfCycleDeps])

  useEffect(() => {
    if (timeUntilEnd === 0 && endOfCycle) {
      getRewardFromService({
        account,
        accessToken
      })
    }
  }, [timeUntilEnd, accessToken, account, endOfCycleDeps, getRewardFromService])

  return (
    <ContentCard fullWidth height="100%" flexDirection="column" p={isTablet ? '24px 32px' : '24px'}>
      <Flex gap="8px" alignItems="center" mb="40px">
        <StyledBiennale variant={TextVariant.BODY_BIG_BOLD}>{t('rewards.untilNextCycle')}</StyledBiennale>
        <InfoIcon
          iconProps={{ size: 18 }}
          tooltipProps={{
            title: t('rewards.untilNextCycleInfo'),
            placement: 'top'
          }}
        />
      </Flex>
      <Text
        variant={isTablet ? TextVariant.BODY_DEFAULT_BOLD : TextVariant.BODY_SMALL_BOLD}
        color={colors.purple}
        mb={isTablet ? '28px' : '8px'}
        textAlign="center"
      >
        {formattedEndOfCycleDateToDisplay}
      </Text>
      <ProgressContainer>
        <Circle>
          <Flex flexDirection="column" alignItems="center">
            <Text
              variant={isTablet ? TextVariant.BODY_DEFAULT : TextVariant.BODY_SMALL}
              color={colors.gray6}
              textAlign="center"
            >
              {t('rewards.cycleEndsIn')}
            </Text>
            <Text
              variant={isTablet ? TextVariant.H4 : TextVariant.BODY_DEFAULT_BOLD}
              color={colors.white}
              textAlign="center"
            >
              {addLeadingZeros(
                displayDuration({
                  time: timeUntilEnd,
                  options: {
                    units: ['h', 'm', 's']
                  }
                })
              )}
            </Text>
          </Flex>
        </Circle>
        <ProgressWrapper zIndex={1}>
          <Progress size={progressSize} thickness={2} variant="determinate" value={progress} />
        </ProgressWrapper>
        <ProgressWrapper>
          <ProgressShadow size={progressSize} thickness={2} variant="determinate" value={100} />
        </ProgressWrapper>
        <DotBig />
        <DotSmall />
      </ProgressContainer>
    </ContentCard>
  )
}

export default UntilNewCycleCounter
